import React, { FC, useState, useEffect, useMemo } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'

import { LangValue } from '../../../../states/types/common'

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  updateCurrentDate, updateCurrentEndDate, updateCurrentMonth, updateCurrentReportRangeType, updateCurrentStartDate,
} from '../../../../states/containers/common/currentSlice'
import {
  fetchMemberReportExists,
  fetchReportMember,
  fetchReportUserData,
  fetchReportCategoryWork,
  fetchApplicationProportion,
  fetchApplicationWorkTime,
  fetchApplicationTimeLine,
  resetState,
} from '../../../../states/containers/member/reportSlice'
import {
  fetchMemberReportExists as fetchMonthlyMemberReportExists,
  fetchReportMember as fetchMonthlyReportMember,
  fetchReportUserData as fetchMonthlyReportUserData,
  fetchReportCategoryWork as fetchMonthlyReportCategoryWork,
  fetchApplicationProportion as fetchMonthlyApplicationProportion,
  fetchApplicationWorkTime as fetchMonthlyApplicationWorkTime,
  fetchApplicationTimeLine as fetchMonthlyApplicationTimeLine,
  fetchMixChart as fetchMonthlyMixChart,
  fetchHeatMap as fetchMonthlyHeatMap,
  resetState as resetMonthlyState,
} from '../../../../states/containers/member/monthlyReportSlice'
import {
  fetchMemberReportExists as fetchCustomMemberReportExists,
  fetchReportMember as fetchCustomReportMember,
  fetchReportUserData as fetchCustomReportUserData,
  fetchReportCategoryWork as fetchCustomReportCategoryWork,
  fetchApplicationProportion as fetchCustomApplicationProportion,
  fetchApplicationWorkTime as fetchCustomApplicationWorkTime,
  fetchApplicationTimeLine as fetchCustomApplicationTimeLine,
  fetchMixChart as fetchCustomMixChart,
  fetchHeatMap as fetchCustomHeatMap,
  resetState as resetCustomState,
} from '../../../../states/containers/member/customReportSlice'

import { URL } from '../../../constants/url'
import {
  endOfMonth,
  format,
  nowDate,
  startOfMonth,
} from '../../../../utils/date'
import { getGetPrams } from '../../../../utils/url'

import Loading from '../../../components/common/Loading'

import CategoryWorkTime from '../../../components/risk/CategoryWorkTime'
import ApplicationProportion from '../../../components/risk/ApplicationProportion'
import ApplicationWorkTime from '../../../components/risk/ApplicationWorkTime'
import ApplicationTimeLine from '../../../components/risk/ApplicationTimeline'
import UserData from '../../../components/risk/UserData'
import MemberDailyIntro from '../../../components/risk/MemberDailyIntro'
import MemberMonthlyIntro from '../../../components/risk/MemberMonthlyIntro'
import { hasRead } from '../../../../utils/introPage'
import { createIntroPage } from '../../../../states/containers/common/introPagesSlice'
import { mapAllowedDate } from '../../../../utils/plan'
import RangeBlock from '../../../components/risk/RangeBlock'
import MemberContentsHeader from '../../../components/risk/MemberContentsHeader'
import ApplicationWorkValue from '../../../components/risk/ApplicationWorkValue'
import MixChart from '../../../components/risk/MixChart'
import MemberSummaryModule from '../../../components/task/MemberSummaryModule'
import { fetchTaskReportMember } from '../../../../states/containers/task/taskSlice'
import PdfModule from '../../../components/common/PdfModule'
import Timeline from '../../../components/common/Timeline'
import { fetchRiskTimeline } from '../../../../states/containers/risk/riskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import InfoTips from '../../../components/common/InfoTips'

interface GetParams {
  target_date: string
  target_month: string
  target_start_date: string
  target_end_date: string
  type: string
}

interface FilterParams {
  category: string | undefined
  application: string | undefined
}

const Detail: FC = () => {
  const {
    commonPart,
    current,
    dailyMemberReport,
    monthlyMemberReport,
    customMemberReport,
    task,
    risk,
  } = useSelector((state: RootState) => state)

  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const { id } = useParams<{ id: string | undefined }>()
  const { language } = useParams<{ language: LangValue }>()

  const [introShow, setIntroShow] = useState(false)
  const [filter, setFilter] = useState<FilterParams>({ category: undefined, application: undefined })

  const [selectRange, setSelectRange] = useState<string | null>("")

  const [isLoadAction, setIsLoadAction] = useState<boolean>(false)

  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const pdfTitle = useMemo(() => {
    let rangeText = ""
    if (selectRange === "daily") {
      rangeText = format(current.targetDate, "YYYY年MM月DD日")
    } else if (selectRange === "monthly") {
      rangeText = format(`${current.targetMonth}01`, "YYYY年MM月")
    } else {
      rangeText = `${format(current.targetStartDate, "YYYY年MM月DD日")} ~ ${format(current.targetEndDate, "YYYY年MM月DD日")}`
    }
    return `Qaseeアセスメント個人レポート ${commonPart.members?.find((val) => val.id === Number(id))?.name} ${rangeText}`
  }, [selectRange, id, current])

  const updateData = (rangeType: string | null) => {
    if (!commonPart.company || !rangeType) return
    if (rangeType === 'daily') {
      const requestParam = {
        member_id: Number(id),
        target_date: current.targetDate,
      }

      dispatch(fetchReportUserData(requestParam))
      dispatch(fetchReportCategoryWork(requestParam))
      dispatch(fetchApplicationProportion(requestParam))
      dispatch(fetchApplicationWorkTime(requestParam))
      dispatch(fetchApplicationTimeLine(requestParam))
      dispatch(fetchRiskTimeline({ target_date: requestParam.target_date, member_ids: [Number(id)], page: 1, limit: 1 }))
    } else if (rangeType === 'monthly') {
      const requestParam = {
        member_id: Number(id),
        target_month: current.targetMonth,
      }

      dispatch(fetchMonthlyReportUserData(requestParam))
      dispatch(fetchMonthlyReportCategoryWork(requestParam))
      dispatch(fetchMonthlyApplicationProportion(requestParam))
      dispatch(fetchMonthlyApplicationWorkTime(requestParam))
      dispatch(fetchMonthlyApplicationTimeLine(requestParam))
      dispatch(fetchMonthlyMixChart(requestParam))
      dispatch(fetchMonthlyHeatMap(requestParam))
    } else if (rangeType === 'custom') {
      const requestParam = {
        member_id: Number(id),
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }

      dispatch(fetchCustomReportUserData(requestParam))
      dispatch(fetchCustomReportCategoryWork(requestParam))
      dispatch(fetchCustomApplicationProportion(requestParam))
      dispatch(fetchCustomApplicationWorkTime(requestParam))
      dispatch(fetchCustomApplicationTimeLine(requestParam))
      dispatch(fetchCustomMixChart(requestParam))
      dispatch(fetchCustomHeatMap(requestParam))
    }
  }

  useEffect(() => {
    if (!commonPart.company) return
    const searchParams = location.search
    const getParams = getGetPrams(searchParams) as GetParams
    const type = getParams.type || current.reportRangeType || "daily"
    dispatch(updateCurrentReportRangeType(type))
    setSelectRange(type)
    const targetDateParam = getParams.target_date || current.targetDate
    dispatch(updateCurrentDate(mapAllowedDate(targetDateParam, commonPart.company.plan, commonPart.company.created_at)))
    const targetMonthParam = getParams.target_month || current.targetMonth
    dispatch(updateCurrentMonth(targetMonthParam))
    const targetStartDateParam = getParams.target_start_date || format(current.targetStartDate, 'YYYY-MM-DD')
    dispatch(updateCurrentStartDate(targetStartDateParam))
    const targetEndDateParam = getParams.target_end_date || format(current.targetEndDate, 'YYYY-MM-DD')
    dispatch(updateCurrentEndDate(targetEndDateParam))
    setIsLoadAction(true)
  }, [location])

  useEffect(() => {
    if (!id || !isLoadAction) return
    if (current.reportRangeType === 'daily') {
      dispatch(resetState())
      dispatch(fetchMemberReportExists({
        member_id: Number(id),
        target_date: current.targetDate,
      }))
      dispatch(fetchReportMember({ member_id: Number(id) }))
      dispatch(fetchTaskReportMember({
        id: id || "0",
        start_date: current.targetDate,
        end_date: current.targetDate,
      }))
    }
    if (current.reportRangeType === 'monthly') {
      dispatch(resetMonthlyState())
      dispatch(fetchMonthlyMemberReportExists({
        member_id: Number(id),
        target_month: current.targetMonth,
      }))
      dispatch(fetchMonthlyReportMember({ member_id: Number(id) }))
      dispatch(fetchTaskReportMember({
        id: id || "0",
        start_date: startOfMonth(`${current.targetMonth}01`, "YYYY-MM-DD"),
        end_date: endOfMonth(`${current.targetMonth}01`, "YYYY-MM-DD"),
      }))
    }
    if (current.reportRangeType === 'custom') {
      dispatch(resetCustomState())
      dispatch(fetchCustomMemberReportExists({
        member_id: Number(id),
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }))
      dispatch(fetchCustomReportMember({ member_id: Number(id) }))
      dispatch(fetchTaskReportMember({
        id: id || "0",
        start_date: current.targetStartDate,
        end_date: current.targetEndDate,
      }))
    }
    setIsLoadAction(false)
  }, [isLoadAction])

  useEffect(() => {
    if (dailyMemberReport.userProcessExists) {
      updateData(selectRange)
    }
  }, [dailyMemberReport.userProcessExists])

  useEffect(() => {
    if (monthlyMemberReport.evaluationExists) {
      updateData(selectRange)
    }
  }, [monthlyMemberReport.evaluationExists])

  useEffect(() => {
    if (customMemberReport.evaluationExists) {
      updateData(selectRange)
    }
  }, [customMemberReport.evaluationExists])

  useEffect(() => {
    if (!commonPart.read_first_intro_ids || language === 'en') return
    const showIntro = !hasRead(URL.RISK_MEMBER_DETAIL.path, commonPart.read_first_intro_ids)
    setIntroShow(showIntro)
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeDate = (value: any) => {
    history.push(`?type=daily&target_date=${format(value, 'YYYY-MM-DD')}`)
  }

  const handleChangeMonth = (value: number) => {
    history.push(`?type=monthly&target_month=${value}`)
  }

  const handleChangeRange = (value: {startDate: string, endDate: string}) => {
    if (value.startDate !== value.endDate) {
      history.push(`?type=custom&target_start_date=${value.startDate}&target_end_date=${value.endDate}`)
    }
  }

  const initialFilter = () => setFilter({ category: undefined, application: undefined })
  const switchCategoryFilter = (categoryFilter: string) => setFilter({ category: categoryFilter, application: undefined })
  const switchApplicationFilter = (applicationFilter: string) => setFilter({ category: undefined, application: applicationFilter })

  const renderNoContentHeadBody = () => (
    <>
      <div style={{ paddingTop: "4em", textAlign: "center" }}>
        <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.report.no_data.title')}</span>
      </div>
      <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
        <span style={{ fontSize: "16px", fontWeight: "bold" }}>{t('member.report.no_data.description')}</span>
      </div>
      <div style={{ paddingTop: "1.5em", textAlign: "center" }}>
        <p style={{ fontSize: "12px", color: "rgba(51, 51, 51, 0.75)" }}>
          {t('member.report.no_data.contact_first')}
          <br />
          {t('member.report.no_data.contact_second')}
          <br />
        </p>
      </div>
    </>
  )

  const renderExistsHeadBody = () => (
    <>
      {
        format(current.targetDate) === nowDate() && (
          <div>
            <div className="error_box">
              分類別作業時間、アプリ使用割合、工数管理情報、アプリ別作業時間などは、データ集計中のため翌日以降に表示されます
            </div>
          </div>
        )
      }
      <UserData
        isLoading={dailyMemberReport.isUserDataLoading}
        userData={dailyMemberReport.userData}
        mode="daily"
        isTeamManager={false}
      />
      <div
        style={{
          margin: "0 0.5em",
        }}
      >
        <div className="box">
          <div
            style={{
              fontSize: "24px",
              color: "#094e8d",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            操作量と作業内容
          </div>
          <InfoTips>
            <>
              <div>
                10分ごとの操作量の推移と主な作業内容を表示しています
              </div>
            </>
          </InfoTips>
          {
            risk.isLoading ? (
              <LoadingSquare>
                <>
                  タイムライン情報を取得しています…
                </>
              </LoadingSquare>
            ) : (
              <Timeline
                member={risk.riskTimeline?.members[0]}
                targetDate={current.targetDate || nowDate()}
                displayHeader={false}
                maxValue={risk.riskTimeline?.max_action_value}
              />
            )
          }
        </div>
      </div>
    </>
  )

  const renderDataGenerationHeadBody = () => (
    <>
      <div style={{ paddingTop: "4em", textAlign: "center" }}>
        <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.report.no_evaluation.title')}</span>
      </div>
      <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
        {/* eslint-disable react/no-danger */}
        <span
          style={{ fontSize: "16px", fontWeight: "bold" }}
          dangerouslySetInnerHTML={{ __html: t('member.report.no_evaluation.description') }}
        />
        {/* eslint-enable react/no-danger */}
      </div>
      <div style={{ paddingTop: "1.5em", textAlign: "center" }}>
        <p style={{ fontSize: "12px", color: "rgba(51, 51, 51, 0.75)" }}>
          {t('member.report.no_evaluation.contact_first')}
        </p>
      </div>
    </>
  )

  const renderDisApproveHeadBody = () => (
    <div style={{ paddingTop: "4em", textAlign: "center" }}>
      <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.report.delete.title')}</span>
    </div>
  )

  const renderDailyHead = () => {
    if (dailyMemberReport.isDisApprove) return renderDisApproveHeadBody()
    if (dailyMemberReport.evaluationExists || (dailyMemberReport.userProcessExists && format(current.targetDate) === nowDate())) {
      return renderExistsHeadBody()
    }
    if (!dailyMemberReport.userProcessExists && format(current.targetDate) === nowDate()) return renderNoContentHeadBody()
    if (dailyMemberReport.userProcessExists) return renderDataGenerationHeadBody()
    return renderNoContentHeadBody()
  }

  const handleOpenIntroModal = () => setIntroShow(true)
  const handleCloseIntroModal = () => {
    setIntroShow(false)
    dispatch(
      createIntroPage(
        URL.RISK_MEMBER_DETAIL.path,
        () => { /* do nothing */ },
      ),
    )
  }

  const renderDailyBody = () => (
    dailyMemberReport.evaluationExists && format(current.targetDate) !== nowDate() && (
      <div style={{ display: "flex" }}>
        <div className="col-xs-9" style={{ width: "70%", paddingRight: "0em", paddingLeft: "0em" }}>
          <CategoryWorkTime
            isLoading={dailyMemberReport.isCategoryWorkLoading}
            categoryWork={dailyMemberReport.categoryWork}
            setCategoryFilter={switchCategoryFilter}
            mode="daily"
          />
          <ApplicationProportion
            isLoading={dailyMemberReport.isApplicationProportion}
            applicationProportion={dailyMemberReport.applicationProportion}
            date={current.targetDate}
            setApplicationFilter={switchApplicationFilter}
            mode="daily"
          />
          <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
            <div className="box summary">
              <MemberSummaryModule
                member={task.taskReportMember}
              />
            </div>
          </div>
          <ApplicationWorkTime
            isLoading={dailyMemberReport.isApplicationWorkTime}
            applicationWorkTime={dailyMemberReport.applicationWorkTime}
            date={current.targetDate}
            setApplicationFilter={switchApplicationFilter}
            mode="daily"
          />
        </div>
        <div className="col-xs-3" style={{ width: "30%", padding: "0em", paddingRight: "0.5em" }}>
          <ApplicationTimeLine
            isLoading={dailyMemberReport.isApplicationTimeLine}
            applicationTimeLine={dailyMemberReport.applicationTimeLine}
            categoryFilter={filter.category}
            applicationFilter={filter.application}
            setInitialFilter={initialFilter}
            mode="daily"
            date={current.targetDate}
          />
        </div>
      </div>
    )
  )

  const renderMonthlyHead = () => {
    if (monthlyMemberReport.evaluationExists) {
      return (
        <>
          <UserData
            isLoading={monthlyMemberReport.isUserDataLoading}
            userData={monthlyMemberReport.userData}
            mode="monthly"
            isTeamManager={false}
          />
          <MixChart
            isLoading={monthlyMemberReport.isMixChartLoading}
            mixChart={monthlyMemberReport.mixChart}
          />
        </>
      )
    }
    if (monthlyMemberReport.userProcessExists) {
      return (
        <>
          <div style={{ paddingTop: "4em", textAlign: "center" }}>
            <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.monthly_report.no_evaluation.title')}</span>
          </div>
          <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
            {/* eslint-disable react/no-danger */}
            <span
              style={{ fontSize: "16px", fontWeight: "bold" }}
              dangerouslySetInnerHTML={{ __html: t('member.monthly_report.no_evaluation.description') }}
            />
            {/* eslint-enable react/no-danger */}
          </div>
          <div style={{ paddingTop: "1.5em", textAlign: "center" }}>
            <p style={{ fontSize: "12px", color: "rgba(51, 51, 51, 0.75)" }}>
              {t('member.monthly_report.no_evaluation.contact_first')}
            </p>
          </div>
        </>
      )
    }

    return (
      <>
        {
          monthlyMemberReport.isDisApprove ? (
            <div style={{ paddingTop: "4em", textAlign: "center" }}>
              <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.monthly_report.delete.title')}</span>
            </div>
          ) : (
            <>
              <div style={{ paddingTop: "4em", textAlign: "center" }}>
                <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t("member.monthly_report.no_data")}</span>
              </div>
            </>
          )
        }
      </>
    )
  }

  const renderMonthlyBody = () => (
    (!monthlyMemberReport.isDataExistsLoading && monthlyMemberReport.evaluationExists
      && monthlyMemberReport.isDisApprove !== undefined) && (
      <div style={{ display: "flex" }}>
        <div className="col-xs-9" style={{ width: "70%", paddingRight: "0em", paddingLeft: "0em" }}>
          <CategoryWorkTime
            isLoading={monthlyMemberReport.isCategoryWorkLoading}
            categoryWork={monthlyMemberReport.categoryWork}
            setCategoryFilter={switchCategoryFilter}
            mode="monthly"
          />
          <ApplicationProportion
            isLoading={monthlyMemberReport.isApplicationProportion}
            applicationProportion={monthlyMemberReport.applicationProportion}
            date={current.targetMonth}
            setApplicationFilter={switchApplicationFilter}
            mode="monthly"
          />
          <ApplicationWorkValue
            isLoading={monthlyMemberReport.isHeatMapLoading}
            heatMap={monthlyMemberReport.heatMap}
          />
          <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
            <div className="box summary">
              <MemberSummaryModule
                member={task.taskReportMember}
              />
            </div>
          </div>
          <ApplicationWorkTime
            isLoading={monthlyMemberReport.isApplicationWorkTime}
            applicationWorkTime={monthlyMemberReport.applicationWorkTime}
            date={current.targetMonth}
            setApplicationFilter={switchApplicationFilter}
            mode="monthly"
          />
        </div>
        <div className="col-xs-3" style={{ width: "30%", padding: "0em", paddingRight: "0.5em" }}>
          <ApplicationTimeLine
            isLoading={monthlyMemberReport.isApplicationTimeLine}
            applicationTimeLine={monthlyMemberReport.applicationTimeLine}
            categoryFilter={filter.category}
            applicationFilter={filter.application}
            setInitialFilter={initialFilter}
            mode="monthly"
            date={current.targetMonth}
          />
        </div>

      </div>
    )
  )

  const renderCustomHead = () => {
    if (customMemberReport.evaluationExists) {
      return (
        <>
          <UserData
            isLoading={customMemberReport.isUserDataLoading}
            userData={customMemberReport.userData}
            mode="custom"
            isTeamManager={false}
          />
          <MixChart
            isLoading={customMemberReport.isMixChartLoading}
            mixChart={customMemberReport.mixChart}
          />
        </>
      )
    }
    if (customMemberReport.userProcessExists) {
      return (
        <>
          <div style={{ paddingTop: "4em", textAlign: "center" }}>
            <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.custom_range_report.no_evaluation.title')}</span>
          </div>
          <div style={{ paddingTop: "0.25em", textAlign: "center" }}>
            {/* eslint-disable react/no-danger */}
            <span
              style={{ fontSize: "16px", fontWeight: "bold" }}
              dangerouslySetInnerHTML={{ __html: t('member.custom_range_report.no_evaluation.description') }}
            />
            {/* eslint-enable react/no-danger */}
          </div>
          <div style={{ paddingTop: "1.5em", textAlign: "center" }}>
            <p style={{ fontSize: "12px", color: "rgba(51, 51, 51, 0.75)" }}>
              {t('member.custom_range_report.no_evaluation.contact_first')}
            </p>
          </div>
        </>
      )
    }

    return (
      <>
        {
          customMemberReport.isDisApprove ? (
            <div style={{ paddingTop: "4em", textAlign: "center" }}>
              <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t('member.custom_range_report.delete.title')}</span>
            </div>
          ) : (
            <>
              <div style={{ paddingTop: "4em", textAlign: "center" }}>
                <span style={{ fontSize: "32px", color: "#094E8D", fontWeight: "bold" }}>{t("member.custom_range_report.no_data")}</span>
              </div>
            </>
          )
        }
      </>
    )
  }

  const renderCustomBody = () => (
    (!customMemberReport.isDataExistsLoading && customMemberReport.evaluationExists
      && customMemberReport.isDisApprove !== undefined) && (
      <div style={{ display: "flex" }}>
        <div className="col-xs-9" style={{ width: "70%", paddingRight: "0em", paddingLeft: "0em" }}>
          <CategoryWorkTime
            isLoading={customMemberReport.isCategoryWorkLoading}
            categoryWork={customMemberReport.categoryWork}
            setCategoryFilter={switchCategoryFilter}
            mode="custom"
          />
          <ApplicationProportion
            isLoading={customMemberReport.isApplicationProportion}
            applicationProportion={customMemberReport.applicationProportion}
            date={{ start_date: current.targetStartDate, end_date: current.targetEndDate }}
            setApplicationFilter={switchApplicationFilter}
            mode="custom"
          />
          <ApplicationWorkValue
            isLoading={customMemberReport.isHeatMapLoading}
            heatMap={customMemberReport.heatMap}
          />
          <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
            <div className="box summary">
              <MemberSummaryModule
                member={task.taskReportMember}
              />
            </div>
          </div>
          <ApplicationWorkTime
            isLoading={customMemberReport.isApplicationWorkTime}
            applicationWorkTime={customMemberReport.applicationWorkTime}
            date={{ start_date: current.targetStartDate, end_date: current.targetEndDate }}
            setApplicationFilter={switchApplicationFilter}
            mode="custom"
          />
        </div>
        <div className="col-xs-3" style={{ width: "30%", padding: "0em", paddingRight: "0.5em" }}>
          <ApplicationTimeLine
            isLoading={customMemberReport.isApplicationTimeLine}
            applicationTimeLine={customMemberReport.applicationTimeLine}
            categoryFilter={filter.category}
            applicationFilter={filter.application}
            setInitialFilter={initialFilter}
            mode="custom"
            date={{ start_date: current.targetStartDate, end_date: current.targetEndDate }}
          />
        </div>
      </div>
    )
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderBody = ():any => {
    if (selectRange === "daily") {
      return (
        <>
          {
            // eslint-disable-next-line max-len
            dailyMemberReport.isDataExistsLoading
              || dailyMemberReport.evaluationExists === undefined
              || dailyMemberReport.userProcessExists === undefined
              || dailyMemberReport.isDisApprove === undefined
              ? (
                <Loading />
              ) : (
                renderDailyHead()
              )
          }
          {renderDailyBody()}
        </>
      )
    }
    if (selectRange === "monthly") {
      return (
        <>
          {
            // eslint-disable-next-line max-len
            monthlyMemberReport.isDataExistsLoading
              || monthlyMemberReport.evaluationExists === undefined
              || monthlyMemberReport.userProcessExists === undefined
              || monthlyMemberReport.isDisApprove === undefined
              ? (
                <Loading />
              ) : (
                renderMonthlyHead()
              )
          }
          {renderMonthlyBody()}
        </>
      )
    }
    return (
      <>
        {
          // eslint-disable-next-line max-len
          customMemberReport.isDataExistsLoading
            || customMemberReport.evaluationExists === undefined
            || customMemberReport.userProcessExists === undefined
            || customMemberReport.isDisApprove === undefined
            ? (
              <Loading />
            ) : (
              renderCustomHead()
            )
        }
        {renderCustomBody()}
      </>
    )
  }

  useEffect(() => {
    if (
      (selectRange === "daily" && dailyMemberReport.isDataExistsLoading)
      || (selectRange === "monthly" && monthlyMemberReport.isDataExistsLoading)
      || (selectRange === "custom" && customMemberReport.isDataExistsLoading)
      || task.isLoading
    ) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [dailyMemberReport, monthlyMemberReport, customMemberReport, task])

  return (
    <section className="risk_page">
      <Helmet>
        <title>Qasee - リスクアセスメント - 個人データ</title>
      </Helmet>
      <section className="breadcrumb">
        リスクアセスメント
        &nbsp;&gt;&nbsp;
        個人データ
        &nbsp;
      </section>
      <section className="page_header">
        <h1 className="question">
          個人データ
          <span className="period">{t(`module.${current.reportRangeType}`)}</span>
          &nbsp;
          {
            language === 'ja' && (
              <div className="icon_question" onClick={handleOpenIntroModal} role="none">
                <i className="fas fa-question-circle" />
              </div>
            )
          }
        </h1>
        <RangeBlock
          changeDateCallback={handleChangeDate}
          changeMonthCallback={handleChangeMonth}
          changeRangeCallback={handleChangeRange}
        />
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={pdfTitle}
            target="pdf_area"
            disabled={isDisablePdf}
          />
        </div>
      </section>
      <section id="pdf_area">
        <section className="page_contents content-area clear">
          <section className="content">
            <div className="row">
              <MemberContentsHeader />
              {renderBody()}
            </div>
          </section>
        </section>
        {
          current.reportRangeType === 'daily' ? (
            <MemberDailyIntro isOpen={introShow} onCloseModal={handleCloseIntroModal} />
          ) : (
            <MemberMonthlyIntro isOpen={introShow} onCloseModal={handleCloseIntroModal} />
          )
        }
      </section>
    </section>
  )
}

export default Detail
